import { useSnackbar } from 'notistack5';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GENERAL_ENQUETE_JSON_INPUT_TYPE, GeneralEnquete, Question } from 'src/@types/generalEnquete';
import { usePermissionApi } from 'src/api/usePermissionApi';
import { RespondentRequestEntity } from 'src/api/useRespondentRequestApi';
import { SurveyDetailAction5Entity } from 'src/api/useSurveyDetailAction5Api';
import { SurveyDetailEntity } from 'src/api/useSurveyDetailApi';
import { useTranslationUpload } from 'src/features/general/enquete-create/components/modals/enquete-translation-support/hooks/useTranslationUpload';
import { useEnqueteCreateGrid } from 'src/features/general/enquete-create/hooks/useEnqueteCreateGrid';
import { ENQUETE_CREATE_PATH } from 'src/features/general/enquete-create/routes/path';
import { MAX_DATA_SIZE } from 'src/generalConstants';
import { setPermission } from 'src/redux/slices/pageSlice';
import { RootState, useAppDispatch, useAppSelector } from 'src/redux/store';
import { setEnqueteData, setQuestions, setQuestionsEng } from '../store/enqueteCreateSlice';

export const useEnqueteSave = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const questions = useAppSelector((state: RootState) => state.enqueteCreate.questions);
  const questionsTranslationData = useAppSelector((state: RootState) => state.enqueteCreate.questionsTranslationData);
  const questionDescriptionJpn = useAppSelector((state: RootState) => state.enqueteCreate.questionDescriptionJpn);
  const questionDescriptionEng = useAppSelector((state: RootState) => state.enqueteCreate.questionDescriptionEng);
  const nav = useNavigate();
  const enqueteCreateGrid = useEnqueteCreateGrid();
  const { replaceTranslationQuestionsData, convertFileArrayToTranslationTable } = useTranslationUpload();
  const { checkPermission } = usePermissionApi();

  const onClickSaveData = async (enqueteData: RespondentRequestEntity, logicQuestions?: Question[]) => {
    const newEnqueteData: RespondentRequestEntity = JSON.parse(JSON.stringify(enqueteData));
    const tabData: GeneralEnquete = newEnqueteData.surveyDetail?.surveyFormData?.formData.tabData[0] as GeneralEnquete;

    const currentTranslationTable = convertFileArrayToTranslationTable(questionsTranslationData);

    if (logicQuestions) {
      tabData.questionsJpn = logicQuestions;
      tabData.questionsEng = replaceTranslationQuestionsData(logicQuestions, currentTranslationTable);
    } else {
      tabData.questionsJpn = questions;
      tabData.questionsEng = replaceTranslationQuestionsData(questions, currentTranslationTable);
    }
    // 「カレンダー反映：点検実施期間」の重複チェック
    const periodCount = tabData.questionsJpn.filter(
      (v: Question) => v.inputType === GENERAL_ENQUETE_JSON_INPUT_TYPE.DATERANGE && v.validation?.isCalendarSync === true,
    ).length;
    if (periodCount > 1) {
      enqueueSnackbar(t('message.periodDuplicate'), {
        variant: 'error',
      });
      return;
    }

    // 「カレンダー反映：現地現物確認予定日」の禁止チェック
    const hasDateCalendarSync = tabData.questionsJpn.some(
      (v: Question) => v.inputType === GENERAL_ENQUETE_JSON_INPUT_TYPE.DATE && v.validation?.isCalendarSync === true,
    );
    if (hasDateCalendarSync) {
      enqueueSnackbar(t('message.dateNotAllowed'), {
        variant: 'error',
      });
      return;
    }

    tabData.questionsTranslationData = questionsTranslationData;
    tabData.questionDescriptionJpn = questionDescriptionJpn;
    tabData.questionDescriptionEng = questionDescriptionEng;

    if (JSON.stringify(tabData).length > MAX_DATA_SIZE.SURVEY_FORM) {
      enqueueSnackbar(t('message.exceptionDataSizeOver', { target: t('common.question') }), { variant: 'error' });
    } else {
      if (!newEnqueteData.surveyDetailId) {
        const result = await enqueteCreateGrid.saveNewEnqueteData(newEnqueteData, 'general');
        if (result) {
          dispatch(setEnqueteData({ ...result.respondentRequest, surveyDetail: { ...result.surveyDetail, surveyHeader: result.surveyHeader } }));
          // 下位ComponentでPermissionを使用するためにfetchした時のパラメータをStateにセットする
          await checkPermission({ respondentRequestId: result.respondentRequest.id }).then((result) => {
            dispatch(setPermission(result));
          });
          nav(ENQUETE_CREATE_PATH.EDIT + '/' + result.respondentRequest.id);
        }
      } else {
        const survayDetail = newEnqueteData.surveyDetail;
        const result = await enqueteCreateGrid.saveSurveyDetailData(survayDetail as SurveyDetailEntity);
        if (result) {
          newEnqueteData.surveyDetail = {
            ...result.data,
            surveyHeader: newEnqueteData.surveyDetail?.surveyHeader, // memo: 更新後の結果に SurveyHeader が含まれないため、明示的にセット
          } as SurveyDetailEntity;
          dispatch(setEnqueteData({ ...newEnqueteData }));
          dispatch(setQuestions(tabData.questionsJpn as Question[]));
          dispatch(setQuestionsEng(tabData.questionsEng as Question[]));
        }
      }
    }
  };

  // 質問（アクション⑤）を保存
  const onClickSaveAction5Data = async (enqueteData: RespondentRequestEntity, logicQuestions?: Question[]) => {
    const newEnqueteData: RespondentRequestEntity = JSON.parse(JSON.stringify(enqueteData));
    const tabData: GeneralEnquete = (newEnqueteData.surveyDetailAction5?.surveyFormData?.formData.tabData[0] as GeneralEnquete) || {};
    const currentTranslationTable = convertFileArrayToTranslationTable(questionsTranslationData);

    if (logicQuestions) {
      tabData.questionsJpn = logicQuestions;
      tabData.questionsEng = replaceTranslationQuestionsData(logicQuestions, currentTranslationTable);
    } else {
      tabData.questionsJpn = questions;
      tabData.questionsEng = replaceTranslationQuestionsData(questions, currentTranslationTable);
    }

    // 「カレンダー反映：現地現物確認予定日」の重複チェック
    const dateCount = tabData.questionsJpn.filter(
      (v: Question) => v.inputType === GENERAL_ENQUETE_JSON_INPUT_TYPE.DATE && v.validation?.isCalendarSync === true,
    ).length;
    if (dateCount > 1) {
      enqueueSnackbar(t('message.dateDuplicate'), {
        variant: 'error',
      });
      return;
    }

    // 「カレンダー反映：点検実施期間」の禁止チェック
    const hasPeriodCalendarSync = tabData.questionsJpn.some(
      (v: Question) => v.inputType === GENERAL_ENQUETE_JSON_INPUT_TYPE.DATERANGE && v.validation?.isCalendarSync === true,
    );
    if (hasPeriodCalendarSync) {
      enqueueSnackbar(t('message.periodNotAllowed'), {
        variant: 'error',
      });
      return;
    }
    tabData.questionsTranslationData = questionsTranslationData;
    tabData.questionDescriptionJpn = questionDescriptionJpn;
    tabData.questionDescriptionEng = questionDescriptionEng;
    if (JSON.stringify(tabData).length > MAX_DATA_SIZE.SURVEY_FORM) {
      enqueueSnackbar(t('message.exceptionDataSizeOver', { target: t('common.question5') }), { variant: 'error' });
    } else {
      if (!newEnqueteData.surveyDetailAction5Id && !newEnqueteData.surveyDetailId) {
        // 質問と質問（アクション⑤）のどちらもデータがない場合
        const result = await enqueteCreateGrid.saveNewEnqueteData(newEnqueteData, 'general');
        if (result) {
          dispatch(setEnqueteData({ ...result.respondentRequest, surveyDetailAction5: { ...result.surveyDetailAction5 } }));
          // 下位ComponentでPermissionを使用するためにfetchした時のパラメータをStateにセットする
          await checkPermission({ respondentRequestId: result.respondentRequest.id }).then((result) => {
            dispatch(setPermission(result));
          });
          nav(ENQUETE_CREATE_PATH.EDIT + '/' + result.respondentRequest.id);
        }
      } else if (!newEnqueteData.surveyDetailAction5Id && newEnqueteData.surveyDetailId) {
        // 質問にデータがあり、かつ質問（アクション⑤）にデータがない場合
        const result = await enqueteCreateGrid.saveNewSurveyDetailAction5Data(newEnqueteData);
        if (result) {
          newEnqueteData.surveyDetailAction5 = {
            ...result.data,
          } as SurveyDetailAction5Entity;
          newEnqueteData.surveyDetailAction5Id = result.data.id;
          dispatch(setEnqueteData({ ...newEnqueteData }));
          dispatch(setQuestions(tabData.questionsJpn as Question[]));
          dispatch(setQuestionsEng(tabData.questionsEng as Question[]));
        }
      } else {
        // アクション⑤（編集問い合わせ）
        const surveyDetailAction5 = newEnqueteData.surveyDetailAction5;
        const result = await enqueteCreateGrid.saveSurveyDetailAction5Data(surveyDetailAction5 as SurveyDetailAction5Entity);
        if (result) {
          newEnqueteData.surveyDetailAction5 = {
            ...result.data,
          } as SurveyDetailAction5Entity;
          dispatch(setEnqueteData({ ...newEnqueteData }));
          dispatch(setQuestions(tabData.questionsJpn as Question[]));
          dispatch(setQuestionsEng(tabData.questionsEng as Question[]));
        }
      }
    }
  };

  return {
    onClickSaveData,
    onClickSaveAction5Data,
  };
};
